import * as React from 'react';
import { Container, CopyToClipboard, Header, Icon, KeyValuePairs, TextContent } from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { typeToHumanReadableObject } from 'src/components/permissions/myDatasets/common';
import { StatusIcon } from 'src/components/permissions/myDatasets/statusIcon';
import {
  createFgaPolicyViewLink,
  createGroupDetailLink,
  createRequestContextDetailLink,
  createRequestDetailsLink,
  createWorkspaceDetailLink,
} from 'src/routes';
import { flattenDataPermission, getGroupMechanismIdLink, getGroupOrWorkspaceName } from 'src/commons/common';
import {
  ALL_TABLES_RES_NAME,
  CONTACT_CATEGORY,
  CONTACT_CTI,
  CONTACT_ITEM,
  CONTACT_NOTES,
  CONTACT_ONCALL_ROTATION,
  CONTACT_OWNER,
  CONTACT_TYPE,
  CONTACT_WIKI,
  DATA_PERMISSION_CONSUMER_OPTION,
  DATA_PERMISSION_DZ_TYPE,
  DATA_PERMISSION_LAKE_FORMATION_TYPE,
  DATA_PERMISSION_PUBLISHER_OPTION,
  DATA_PERMISSION_REDSHIFT_TYPE,
  DATA_PERMISSION_STATUS_ACTIVE,
  DW_GDP_LF_ACCOUNT,
  DW_GDP_LF_DB_NAME,
  DW_IAD_LF_ACCOUNT,
  DW_IAD_LF_DB_NAME,
  DW_TABLE_NAMES_WITH_MP_DATA,
  LAKEFORMATION_FGA_POLICY_TYPE,
  LAKEFORMATION_TAG_TYPE,
  METADATA_BUSINESS_GLOSSARY,
  METADATA_ENABLED,
  METADATA_FORM,
  REDSHIFT_TAG_TYPE,
  RESOURCE_ITEM,
  RESOURCE_LINK_PREFIX,
  TABLE_RESOURCE_TYPE,
} from 'src/commons/constants';
import {
  getGlossary,
  listGlossaries,
  listMetadataEntitiesAssociatedToResource,
  listMetadataFields,
  listMetadataValues,
} from 'src/api/catalog';
import { GALAXI_PLATFORM_ID_MAP, getGalaxiCatalogEndpointURL, getPartition, getRegion, getStage } from 'src/api/config';
import { getRequest, listDataPermissions } from 'src/api/permissions';
import { isValidGalaxiGroup, isValidPrincipalNotRoot, isValidWorkspace } from 'src/commons/validationUtils';
import { GetDataSetResponseStructure, GlossaryDetails } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import {
  DataPermissionList,
  GetRequestRequest,
  GetRequestResult,
  GladstoneRequestItem,
  ListDataPermissionsRequest,
  Resource,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { GOVERNANCE_TAG_TYPE, GLOBAL_GLOSSARY_TYPE, USER_GLOSSARY_TYPE } from 'src/components/metadata/constants';
import { useEffect, useState } from 'react';

export const getRequestContextIdsForRequestId = async (requestId: string) => {
  const getRequestRequest: GetRequestRequest = {
    requestId: requestId,
  };
  const getRequestResult: GetRequestResult = await getRequest(getRequestRequest);
  const request: GladstoneRequestItem = getRequestResult.requestItem;
  if (request.additionalMetadata?.accessRequestWorkflowInfoList?.length > 0) {
    return request.additionalMetadata.accessRequestWorkflowInfoList
      .filter((metadata) => !!metadata.requestContextId)
      .map((metadata) => metadata.requestContextId);
  }
};

export interface DataPermissionDetailProps {
  dataPermission: any;
}

export const DataPermissionDetail = (props: DataPermissionDetailProps) => {
  const [requestContextIds, setRequestContextIds] = useState<string[]>([]);
  let permissionItem = flattenDataPermission(props.dataPermission);

  useEffect(() => {
    getRequestContextDetails();
  }, [props.dataPermission]);

  const getRequestContextDetails = async () => {
    if (!!permissionItem.permissionRequestId) {
      const rcIds = await getRequestContextIdsForRequestId(permissionItem.permissionRequestId);
      setRequestContextIds(rcIds);
    }
  };

  return (
    <Container header={<Header variant={'h2'}>Data permission details</Header>}>
      <KeyValuePairs
        columns={4}
        items={[
          { label: 'Owner ID', value: permissionItem.ownerId },
          { label: 'Source account', value: permissionItem.catalogId },
          { label: 'Source region', value: permissionItem.region },
          permissionItem.clusterId ? { label: 'Cluster ID', value: permissionItem.clusterId } : undefined,
          permissionItem.tag != null ? { label: 'Tag', value: permissionItem.tag } : undefined,
          permissionItem.tag == null ? { label: 'Source database', value: permissionItem.databaseName } : undefined,
          permissionItem.tag == null && permissionItem.schemaName
            ? { label: 'Schema', value: permissionItem.schemaName }
            : undefined,
          permissionItem.tag == null ? { label: 'Source table', value: permissionItem.tableName } : undefined,
          permissionItem.fgapId
            ? {
                label: 'Fine grain policy',
                value: <Link href={createFgaPolicyViewLink(permissionItem.fgapId)}>{permissionItem.fgapName}</Link>,
              }
            : undefined,
          { label: 'Data lake principal', value: permissionItem.dataLakePrincipal },
          permissionItem.dataConsumerType
            ? { label: 'Data consumer type', value: permissionItem.dataConsumerType }
            : undefined,
          { label: 'Share type', value: typeToHumanReadableObject[permissionItem.type] },
          {
            label: 'Status',
            value: (
              <>
                <StatusIcon status={permissionItem.status} />
                <div>Date: {permissionItem.dateActive}</div>
                {permissionItem.dateInActive !== undefined && permissionItem.dateInActive !== null && (
                  <div>Date inactive: {permissionItem.dateInActive}</div>
                )}
              </>
            ),
          },
          permissionItem.permissionRequestId !== undefined && permissionItem.permissionRequestId !== null
            ? {
                label: 'Request',
                value: (
                  <Link href={createRequestDetailsLink(permissionItem.permissionRequestId)}>
                    {permissionItem.permissionRequestId}
                  </Link>
                ),
              }
            : undefined,
          requestContextIds?.length > 0
            ? {
                label: "Request context ID's",
                value: (
                  <TextContent>
                    <ul>
                      {requestContextIds.map((requestContextId) => (
                        <li>
                          <Link href={createRequestContextDetailLink(requestContextId)}>{requestContextId}</Link>{' '}
                        </li>
                      ))}
                    </ul>
                  </TextContent>
                ),
              }
            : undefined,
          {
            label: 'Audit status',
            value: (
              <>
                <StatusIcon status={permissionItem.auditStatus} />
                <div>Date: {permissionItem.dateOfLastAudit}</div>
              </>
            ),
          },
        ].filter((item) => item != undefined)}
      />
    </Container>
  );
};

export const contactInfoMetadata = (metadataList, contactLevel, resource) => {
  const generateItemName = (itemName) => {
    return `${contactLevel} ${itemName}`;
  };

  let contactMetadata = [];
  let category;
  let type;
  let item;

  for (let metadata of metadataList) {
    let name = metadata.Name;
    let value = metadata.Value;
    switch (name) {
      case CONTACT_CATEGORY:
        category = value;
        break;
      case CONTACT_TYPE:
        type = value;
        break;
      case CONTACT_ITEM:
        item = value;
        break;
      case CONTACT_OWNER:
        contactMetadata.push({ label: generateItemName(name), value: getGroupMechanismIdLink(value) });
        break;
      case CONTACT_WIKI:
        contactMetadata.push({
          label: generateItemName(name),
          value: (
            <Link external={true} href={value}>
              {value}
            </Link>
          ),
        });
        break;
      case CONTACT_ONCALL_ROTATION:
        contactMetadata.push({
          label: generateItemName(name),
          value: (
            <Link external={true} href={value}>
              {value}
            </Link>
          ),
        });
        break;
      case CONTACT_NOTES:
        contactMetadata.push({ label: generateItemName(name), value: value });
        break;
      default:
        contactMetadata.push({
          label: generateItemName(name),
          value: (
            <CopyToClipboard
              variant={'inline'}
              textToCopy={value}
              copySuccessText={'Successfully copied'}
              copyErrorText={'Failed to copy'}
            />
          ),
        });
    }
  }

  if (category && type && item) {
    contactMetadata.push({
      label: generateItemName(CONTACT_CTI),
      value: (
        <Link external={true} href={contactCTILink(category, type, item, resource)}>
          {category}/{type}/{item}
        </Link>
      ),
    });
  }

  return contactMetadata;
};

export const contactCTILink = (category, type, item, resource) => {
  const BASE_URL = 'https://t.corp.amazon.com/create/options?';
  const ticketTitle = `Omni data related issue`;
  const ticketDescription = `Ticket description for Omni resource: ${resource}`;
  const urlParams = new URLSearchParams([
    ['ticket_type', 'Regular Ticket'],
    ['severity', 'FIVE'],
    ['category', category],
    ['type', type],
    ['item', item],
    ['title', ticketTitle],
    ['description', ticketDescription],
  ]);

  return BASE_URL + urlParams.toString();
};

export const getGlossaryDetails = async (glossaryId: string, glossaryOwnerId: string): Promise<GlossaryDetails> => {
  try {
    let getGlossaryResult = await getGlossary({
      OwnerId: glossaryOwnerId,
      GlossaryId: glossaryId,
    });
    return getGlossaryResult.GlossaryDetails;
  } catch (err) {
    console.error('Failed to fetch glossary with id:' + glossaryId, err);
  }
  return undefined;
};

export async function fetchAllGlobalGovernanceTagsInHierarchy(allGlossaries: any[]) {
  const globalGovernanceTags = allGlossaries.filter(
    (glossary) => GOVERNANCE_TAG_TYPE === glossary.GlossarySubType && GLOBAL_GLOSSARY_TYPE === glossary.GlossaryType,
  );
  // convert globalGovernanceTags list to Map with GlossaryId as the key and object as the value
  const globalGovernanceTagMap = new Map(globalGovernanceTags.map((glossary) => [glossary.GlossaryId, glossary]));
  const userGovernanceTags = allGlossaries.filter(
    (glossary) => GOVERNANCE_TAG_TYPE === glossary.GlossarySubType && USER_GLOSSARY_TYPE === glossary.GlossaryType,
  );
  // iterate userGovernanceTags, map to Id and then lookup global glossary
  const allGlobalGovernanceTagParents = new Set(globalGovernanceTags);
  for (const userGlossary of userGovernanceTags) {
    const parentGlossaryId = userGlossary.ParentGlossaryId;
    if (globalGovernanceTagMap.has(parentGlossaryId)) {
      allGlobalGovernanceTagParents.add(globalGovernanceTagMap.get(parentGlossaryId));
      continue;
    }
    // look up parent governance tag if not found in map
    const parentGovernanceTag = await getGlossaryDetails(parentGlossaryId, null);
    if (parentGovernanceTag) allGlobalGovernanceTagParents.add(parentGovernanceTag);
  }
  return [...allGlobalGovernanceTagParents];
}

export const getDistinctObjects = (objList: any[], uniqueId: string): any[] => {
  const uniqueMap = new Map();
  if (!objList) return [];
  return objList.filter((obj) => {
    const key = obj[uniqueId];
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, true);
      return true;
    }
    return false;
  });
};

export const generateMetaDataMap = async (resource) => {
  const generateCombinedFieldValues = async (metadataFormFields, metadataFieldValues) => {
    let newCombinedFieldValues = [];
    for (const field of metadataFormFields) {
      const fieldName = field.Name;
      const fieldType = field.FieldType;
      const fieldId = field.MetadataFieldId;
      const fieldConfig = field.FieldConfig;
      let value = undefined;
      let fieldValue = undefined;
      for (const fieldValueDetail of metadataFieldValues) {
        if (fieldValueDetail.Id == fieldId) {
          fieldValue = fieldValueDetail;
          value = await convertValueToString(fieldValueDetail.Value);
        }
      }
      const item = {
        Name: fieldName,
        FieldType: fieldType,
        Value: value,
        MetadataFieldId: fieldId,
        GlossaryValues: fieldValue?.Value?.GlossaryValueList,
        FieldConfig: fieldConfig,
      };
      if (item.Value != null) {
        newCombinedFieldValues.push(item);
      }
    }
    return newCombinedFieldValues;
  };

  const convertValueToString = async (value) => {
    if (!value) {
      return '';
    }
    if (value.StringValue) {
      return value.StringValue;
    } else if (value.BooleanValue != null) {
      return value.BooleanValue ? 'true' : 'false';
    } else if (value.NumberValue) {
      return String(value.NumberValue);
    } else if (value.GlossaryValueList) {
      const glossaryValueIds = value.GlossaryValueList;
      let glossaryNamesAndIds = [];
      let glossaryDetails;
      for (const glossary of glossaryValueIds) {
        glossaryDetails = await getGlossaryDetails(glossary.GlossaryId, glossary.OwnerId);
        if (glossaryDetails) {
          glossaryNamesAndIds.push({
            Name: glossaryDetails.Name,
            Id: glossaryDetails.GlossaryId,
            GlossaryValue: glossaryDetails.GlossaryValue,
            GlossaryOwnerId: glossaryDetails.OwnerId,
          });
        }
      }
      return (
        <>
          {glossaryNamesAndIds.map((nameAndId) => (
            <>
              <Link href={`/glossaries/${nameAndId.Id}/${nameAndId.GlossaryOwnerId}`}>
                {nameAndId.Name} - {nameAndId.GlossaryValue}
              </Link>
              <br />
            </>
          ))}
        </>
      );
    }
  };

  const getMetadataForms = async (resource) => {
    let formsForResource = [];
    let filteredEnabledForms = [];
    let result = await listMetadataEntitiesAssociatedToResource({
      ResourceArn: resource,
      MetadataEntityFilter: METADATA_FORM,
    });
    filteredEnabledForms = result.FormList.filter((form) => {
      return form.Status == METADATA_ENABLED;
    });
    formsForResource.push(...filteredEnabledForms);
    while (result.NextToken != undefined) {
      result = await listMetadataEntitiesAssociatedToResource({
        ResourceArn: resource,
        MetadataEntityFilter: METADATA_FORM,
        NextToken: result.NextToken,
      });
      filteredEnabledForms = result.FormList.filter((form) => {
        return form.Status == METADATA_ENABLED;
      });
      formsForResource.push(...filteredEnabledForms);
    }
    return formsForResource;
  };

  const getMetaDataFormFields = async (formId) => {
    try {
      let formFields = [];
      let filteredEnabledFields = [];
      let listMetadataFormFieldsResult = await listMetadataFields({
        MetadataFormId: formId,
      });
      filteredEnabledFields = listMetadataFormFieldsResult?.MetadataFieldsList.filter((field) => {
        return field.Status == METADATA_ENABLED;
      });
      formFields.push(...filteredEnabledFields);
      while (listMetadataFormFieldsResult.NextToken != undefined) {
        listMetadataFormFieldsResult = await listMetadataFields({
          MetadataFormId: formId,
          NextToken: listMetadataFormFieldsResult.NextToken,
        });
        filteredEnabledFields = listMetadataFormFieldsResult?.MetadataFieldsList.filter((field) => {
          return field.Status == METADATA_ENABLED;
        });
        formFields.push(...filteredEnabledFields);
      }
      return formFields;
    } catch (err) {
      console.log(`Unable to load fields. ${err.message}`);
    }
  };

  const getMetaDataValuesForField = async (formId) => {
    try {
      let result = await listMetadataValues({
        ResourceArn: resource,
        MetadataFormId: formId,
      });
      let valueList = result?.MetadataValuesList;
      while (result.NextToken != undefined) {
        result = await listMetadataValues({
          ResourceArn: resource,
          MetadataFormId: formId,
          NextToken: result.NextToken,
        });
        valueList.push(...result?.MetadataValuesList);
      }
      return valueList;
    } catch (err) {
      console.log(`Unable to load values. ${err.message}`);
    }
  };

  let metadataForms = await getMetadataForms(resource);
  let map = new Map();
  for (let metadataForm of metadataForms) {
    let metadataFormId = metadataForm.MetadataFormId;
    let metadataFormName = metadataForm.Name;
    let displayFormOn = metadataForm.DisplayFormOn;
    let metadataFormFields = await getMetaDataFormFields(metadataFormId);
    const metadataFormFieldValueList = await getMetaDataValuesForField(metadataFormId);
    const combinedFieldValues = await generateCombinedFieldValues(metadataFormFields, metadataFormFieldValueList);
    map.set(metadataFormName, { values: combinedFieldValues, displayFormOn: displayFormOn });
  }

  return map;
};

export const metadataMapHasCTI = (metadataList) => {
  const [category, type, item] = getCtiFromMetadataMap(metadataList);
  return !!category && !!type && !!item;
};

export const getCtiFromMetadataMap = (metadataList) => {
  let category: string = undefined;
  let type: string = undefined;
  let item: string = undefined;

  if (!!metadataList) {
    for (let metadata of metadataList) {
      if (CONTACT_CATEGORY == metadata.Name) {
        category = metadata.Value;
      }
      if (CONTACT_TYPE == metadata.Name) {
        type = metadata.Value;
      }
      if (CONTACT_ITEM == metadata.Name) {
        item = metadata.Value;
      }
    }
  }

  return [category, type, item];
};

export const getGlossariesForResource = async (resource) => {
  let glossariesForResource = [];
  try {
    // get all glossaries for owner and filter on the results
    let filteredEnabledGlossaries = [];
    let result = await listMetadataEntitiesAssociatedToResource({
      ResourceArn: resource,
      MetadataEntityFilter: METADATA_BUSINESS_GLOSSARY,
    });
    filteredEnabledGlossaries = result.GlossaryList.filter((glossary) => {
      return glossary.Status == METADATA_ENABLED;
    });
    glossariesForResource.push(...filteredEnabledGlossaries);
    while (result.NextToken != undefined) {
      result = await listMetadataEntitiesAssociatedToResource({
        ResourceArn: resource,
        MetadataEntityFilter: METADATA_BUSINESS_GLOSSARY,
        NextToken: result.NextToken,
      });
      filteredEnabledGlossaries = result.GlossaryList.filter((glossary) => {
        return glossary.Status == METADATA_ENABLED;
      });
      glossariesForResource.push(...filteredEnabledGlossaries);
    }
    return glossariesForResource;
  } catch (err) {
    console.log('error', `Unable to load glossaries. ${err.message}`);
  }
  return glossariesForResource;
};

export const getGlossariesForOwner = async (ownerId: string, glossarySubType: string) => {
  let listGlossariesResult = await listGlossaries({
    OwnerId: ownerId,
    GlossarySubType: glossarySubType,
  });
  let glossaries = listGlossariesResult.GlossaryDetailsList;
  while (listGlossariesResult.NextToken != undefined) {
    listGlossariesResult = await listGlossaries({
      OwnerId: ownerId,
      NextToken: listGlossariesResult.NextToken,
      GlossarySubType: glossarySubType,
    });
    glossaries.push(...listGlossariesResult.GlossaryDetailsList);
  }
  // filter glossaries to include only GlossaryType = Global
  return glossaries.filter((glossary) => glossary.GlossaryType === GLOBAL_GLOSSARY_TYPE);
};

// Short term fix to disable customer request db/table level access and use fga instead. Would replace with custom policy.
export const isMPData = (catalogId, databaseName, tableName) => {
  if (catalogId != DW_GDP_LF_ACCOUNT && catalogId != DW_IAD_LF_ACCOUNT) {
    return false;
  }

  if (databaseName != DW_GDP_LF_DB_NAME && databaseName != DW_IAD_LF_DB_NAME) {
    return false;
  }

  return DW_TABLE_NAMES_WITH_MP_DATA.has(tableName);
};

export const getGalaxiSubscriptionURL = (accountId: string, databaseName: string, tableName: string) => {
  const galaxiArn = encodeURIComponent(getGalaxiArn(accountId, databaseName, tableName));
  return `${getGalaxiCatalogEndpointURL()}catalog/details/${galaxiArn}/subscribe`;
};

export const getGalaxiArn = (accountId: string, database: string, table: string, type = 'glue') => {
  const galaxiPlatform = GALAXI_PLATFORM_ID_MAP[getStage()] ?? '';
  return `arn:${getPartition()}:galaxi::${galaxiPlatform}:dataset/${type}/${getRegion()}/${accountId}/${database}/${table}`;
};

export const getGalaxiGroupURL = (galaxiGroupName) => {
  return `${getGalaxiCatalogEndpointURL()}groups/${galaxiGroupName}`;
};

export const loadTableDataPermission = async (activeGroup, dataset) => {
  if (!activeGroup) {
    return;
  }
  const catalogId = dataset?.IdInfo.CatalogId;
  const databaseName = dataset?.IdInfo.DatabaseName;
  const tableName = dataset?.IdInfo.TableName;

  let dataPermissionList = [];
  // check if the current group have All Tables access
  let tableResourceAllTables = {
    table: {
      databaseName: databaseName,
      name: null,
      catalogId: catalogId,
      tableWildcard: {},
    },
  };

  let listAllTablesDataPermissionsNextToken = null;
  do {
    let listAllTablesDataPermissionsRequest = {
      ownerId: activeGroup,
      resource: tableResourceAllTables,
      region: dataset.IdInfo.Region,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      option: DATA_PERMISSION_CONSUMER_OPTION,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: listAllTablesDataPermissionsNextToken,
    };
    let listAllTablesDataPermissionsResult = await listDataPermissions(listAllTablesDataPermissionsRequest);
    dataPermissionList.push(...listAllTablesDataPermissionsResult.dataPermissionList);
    listAllTablesDataPermissionsNextToken = listAllTablesDataPermissionsResult.nextToken;
  } while (listAllTablesDataPermissionsNextToken != null);

  let tableResourceSingleTable = {
    table: {
      databaseName: databaseName,
      name: tableName,
      catalogId: catalogId,
      tableWildcard: null,
    },
  };

  let listSingleTableDataPermissions = null;
  do {
    let listSingleTableDataPermissionsRequest = {
      ownerId: activeGroup,
      resource: tableResourceSingleTable,
      region: dataset.IdInfo.Region,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      option: DATA_PERMISSION_CONSUMER_OPTION,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: listSingleTableDataPermissions,
    };
    let listSingleTableDataPermissionsResult = await listDataPermissions(listSingleTableDataPermissionsRequest);
    dataPermissionList.push(...listSingleTableDataPermissionsResult.dataPermissionList);
    listSingleTableDataPermissions = listSingleTableDataPermissionsResult.nextToken;
  } while (listSingleTableDataPermissions != null);

  return dataPermissionList;
};

export const loadDZTableDataPermission = async (
  consumerGroupOrWorkspaceId: string,
  dataset: GetDataSetResponseStructure,
) => {
  let tableResourceDataZone: Resource = {
    dataZoneResource: {
      databaseName: dataset.IdInfo.DatabaseName,
      tableName: dataset.IdInfo.TableName,
      awsAccountId: dataset.IdInfo.CatalogId,
      clusterId: dataset.IdInfo.ClusterIdentifier,
      redshiftWorkgroupName: dataset.IdInfo.RedshiftWorkgroupName,
      schemaName: dataset.IdInfo.SchemaName,
      region: dataset.IdInfo.Region,
    },
  };
  let dataPermissionList: DataPermissionList = [];

  let nextToken = null;
  do {
    let request = {
      ownerId: consumerGroupOrWorkspaceId,
      resource: tableResourceDataZone,
      region: dataset.IdInfo.Region,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      option: DATA_PERMISSION_CONSUMER_OPTION,
      type: DATA_PERMISSION_DZ_TYPE,
      nextToken: nextToken,
    } as ListDataPermissionsRequest;
    let result = await listDataPermissions(request);
    dataPermissionList.push(...result.dataPermissionList);
    nextToken = result.nextToken;
  } while (nextToken != null);

  return dataPermissionList;
};

interface ConstructTicketParams {
  category?: string;
  type?: string;
  item?: string;
  databaseName?: string;
  datasetName?: string;
  requesterTeamName?: string;
  requesterGAMGroupName?: string;
  requesterEmail?: string;
}

export const constructTicketUrlWithParams = (props: ConstructTicketParams) => {
  const BASE_URL = 'https://t.corp.amazon.com/create/options?';
  const ticketTitle = `New Request for Omni Dataset: ${props.datasetName} from Team:${props.requesterTeamName}`;

  const urlParams = new URLSearchParams([
    ['ticket_type', 'Regular Ticket'],
    ['severity', 'FIVE'],
    ['category', props.category ?? ''],
    ['type', props.type ?? ''],
    ['item', props.item ?? ''],
    ['title', ticketTitle],
    ['description', constructTicketDescription(props)],
  ]);

  const urlWithParams = new URL(BASE_URL + urlParams.toString());
  return urlWithParams;
};

export const constructTicketDescription = (props: ConstructTicketParams) => {
  const description = ` <Enter your ticket description here>

   Requested Omni Dataset:
     Dataset Name: ${props.datasetName}
     Database Name: ${props.databaseName}

   Requester Details:
   Team Name: ${props.requesterTeamName}
   GAM Group Name: ${props.requesterGAMGroupName}
   Email: ${props.requesterEmail}
    `;
  return description;
};

export const lakeFormationIcon = (
  <Icon
    svg={
      <svg xmlns='http://www.w3.org/2000/svg' width='1em' height='1em' viewBox='0 0 256 256'>
        <defs>
          <linearGradient id='logosAwsLakeFormation0' x1='0%' x2='100%' y1='100%' y2='0%'>
            <stop offset='0%' stop-color='#4d27a8' />
            <stop offset='100%' stop-color='#a166ff' />
          </linearGradient>
        </defs>
        <path fill='url(#logosAwsLakeFormation0)' d='M0 0h256v256H0z' />
        <path
          fill='#fff'
          d='M172.6 144.675a13.28 13.28 0 0 0 10.101 4.656c7.335 0 13.3-5.968 13.3-13.302h-6.4a6.907 6.907 0 0 1-6.9 6.902a6.91 6.91 0 0 1-6.902-6.902h-6.4a6.906 6.906 0 0 1-6.899 6.902a6.91 6.91 0 0 1-6.902-6.902h-6.4a6.91 6.91 0 0 1-6.902 6.902a6.91 6.91 0 0 1-6.903-6.902h-6.4c0 3.808-3.097 6.902-6.898 6.902v6.4c4.038 0 7.657-1.808 10.099-4.656a13.28 13.28 0 0 0 10.102 4.656a13.28 13.28 0 0 0 10.102-4.656a13.27 13.27 0 0 0 10.102 4.656a13.28 13.28 0 0 0 10.1-4.656m-2.81-43.539h-6.4c0 3.805-3.098 6.9-6.903 6.9a6.906 6.906 0 0 1-6.899-6.9h-6.4c0 3.805-3.097 6.9-6.905 6.9c-3.801 0-6.899-3.095-6.899-6.9h-6.4c0 3.805-3.097 6.9-6.899 6.9v6.4c4.038 0 7.657-1.809 10.099-4.657a13.28 13.28 0 0 0 10.099 4.656c4.038 0 7.664-1.808 10.105-4.656a13.27 13.27 0 0 0 10.1 4.656a13.28 13.28 0 0 0 10.102-4.656a13.28 13.28 0 0 0 10.099 4.656v-6.4c-3.802 0-6.9-3.094-6.9-6.899m-50.732 87.418c4.035 0 7.657-1.808 10.099-4.656a13.28 13.28 0 0 0 10.102 4.656c4.038 0 7.66-1.808 10.099-4.656a13.28 13.28 0 0 0 10.102 4.656v-6.4c-3.805 0-6.902-3.095-6.902-6.9h-6.4c0 3.805-3.094 6.9-6.899 6.9s-6.902-3.095-6.902-6.9h-6.4c0 3.805-3.098 6.9-6.9 6.9c-3.807 0-6.905-3.095-6.905-6.9h-6.4c0 3.805-3.094 6.9-6.899 6.9v6.4c4.039 0 7.661-1.808 10.1-4.656a13.29 13.29 0 0 0 10.105 4.656M73.603 64h3.2v-3.2h-3.2zM67.2 67.2v-9.6a3.2 3.2 0 0 1 3.203-3.2h9.6a3.2 3.2 0 0 1 3.2 3.2v9.6c0 1.766-1.434 3.2-3.2 3.2h-9.6a3.2 3.2 0 0 1-3.203-3.2m25.602-16h3.2V48h-3.2zm-6.4 3.2v-9.6c0-1.77 1.43-3.2 3.2-3.2h9.6a3.2 3.2 0 0 1 3.2 3.2v9.6c0 1.766-1.433 3.2-3.2 3.2h-9.6a3.2 3.2 0 0 1-3.2-3.2m9.6 19.2h3.2v-3.2h-3.2zm6.4-9.6a3.2 3.2 0 0 1 3.2 3.2v9.6c0 1.766-1.434 3.2-3.2 3.2h-9.6a3.2 3.2 0 0 1-3.2-3.2v-9.6c0-1.77 1.43-3.2 3.2-3.2zM90.201 89.84c-3.248 0-15.331-.125-24.454-1.798l20.156 31.846c.327.512.5 1.104.5 1.712v4.035c2.217.531 4.313.547 6.4.042V121.6c0-.605.169-1.197.492-1.712l20.998-31.792c-7.043 1.248-16.323 1.744-24.092 1.744m-35.59-8.026a5 5 0 0 1-.051-.585c0-1.968 0-7.955 31.778-8.576l.125 6.4c-10.233.198-17.798 1.174-22.108 2.176c4.707 1.097 13.44 2.211 25.846 2.211c11.952 0 20.88-1.075 25.788-2.214c-1.638-.388-3.91-.81-7.04-1.19l.778-6.353c11.443 1.395 16.111 3.584 16.111 7.546c0 .845-.304 2.003-1.388 3.11l-25.248 38.186V128a3.2 3.2 0 0 1-2.006 2.966c-2.46.99-4.931 1.482-7.453 1.482c-2.49 0-5.027-.483-7.648-1.446A3.2 3.2 0 0 1 80.003 128v-5.472L55.44 83.718l-.02-.028l-.025-.045l-.477-.755l.048-.032a3.3 3.3 0 0 1-.355-1.044M86.402 144v-9.6h-6.4v6.4h-6.4a3.2 3.2 0 0 0-2.46 5.248l16 19.2a3.203 3.203 0 0 0 4.918 0l16-19.2a3.198 3.198 0 0 0-2.458-5.248h-6.4v-6.4h-6.4v9.6a3.2 3.2 0 0 0 3.2 3.2h2.768l-9.168 11.002l-9.17-11.002h2.77c1.767 0 3.2-1.434 3.2-3.2m118.398-6.4c0 42.346-34.454 76.8-76.802 76.8c-42.344 0-76.798-34.454-76.798-76.8c0-11.706 2.566-22.94 7.622-33.395l5.763 2.787C59.952 116.57 57.6 126.867 57.6 137.6c0 38.816 31.583 70.4 70.398 70.4c38.819 0 70.402-31.584 70.402-70.4c0-38.82-31.583-70.4-70.402-70.4a71 71 0 0 0-12.246 1.06l-1.104-6.305a77.4 77.4 0 0 1 13.35-1.155c42.348 0 76.802 34.451 76.802 76.8'
        />
      </svg>
    }
    size='big'
  />
);

export const redshiftIcon = (
  <Icon
    svg={
      <svg xmlns='http://www.w3.org/2000/svg' width='1em' height='1em' viewBox='0 0 256 256'>
        <defs>
          <linearGradient id='logosAwsRedshift0' x1='0%' x2='100%' y1='100%' y2='0%'>
            <stop offset='0%' stop-color='#4d27a8' />
            <stop offset='100%' stop-color='#a166ff' />
          </linearGradient>
        </defs>
        <path fill='url(#logosAwsRedshift0)' d='M0 0h256v256H0z' />
        <path
          fill='#fff'
          d='M162.64 112.546c-3.391 0-6.147-2.75-6.147-6.132s2.756-6.136 6.147-6.136c3.388 0 6.144 2.754 6.144 6.136c0 3.381-2.756 6.132-6.144 6.132m-15.57 37.292c-3.388 0-6.143-2.75-6.143-6.132s2.755-6.132 6.143-6.132s6.147 2.75 6.147 6.132s-2.759 6.132-6.147 6.132m-37.367-6.214c-3.385 0-6.14-2.75-6.14-6.132s2.755-6.136 6.14-6.136c3.392 0 6.147 2.754 6.147 6.136c0 3.381-2.755 6.132-6.147 6.132m-15.566 34.185c-3.385 0-6.144-2.75-6.144-6.132s2.759-6.132 6.144-6.132c3.391 0 6.146 2.75 6.146 6.132s-2.755 6.132-6.146 6.132m68.503-83.911c-6.914 0-12.54 5.615-12.54 12.516c0 4.23 2.126 7.96 5.358 10.228l-5.85 14.81a12.6 12.6 0 0 0-2.538-.258c-5.498 0-10.13 3.573-11.814 8.502l-13.09-2.97c-.406-6.537-5.808-11.75-12.463-11.75c-6.91 0-12.533 5.615-12.533 12.516c0 3.225 1.26 6.141 3.276 8.362l-5.712 13.37c-.204-.012-.393-.06-.597-.06c-6.911 0-12.537 5.612-12.537 12.513c0 6.898 5.626 12.513 12.537 12.513c6.914 0 12.54-5.615 12.54-12.513c0-4.428-2.328-8.302-5.815-10.528l5.04-11.795c1.21.386 2.468.65 3.801.65c4.933 0 9.168-2.88 11.21-7.019l13.867 3.146c1.134 5.743 6.21 10.088 12.29 10.088c6.914 0 12.54-5.612 12.54-12.513c0-3.73-1.678-7.044-4.28-9.341l6.147-15.557c.39.039.764.118 1.163.118c6.91 0 12.537-5.615 12.537-12.512c0-6.901-5.626-12.516-12.537-12.516M128 211.219c-31.14 0-54.407-9.348-54.407-17.707V74.085c10.593 8.608 33.016 13.157 54.474 13.157c21.362 0 43.69-4.511 54.34-13.045v119.315c0 8.36-23.27 17.707-54.407 17.707m.067-166.438c32.022 0 54.34 9.507 54.34 18.042c0 8.53-22.318 18.038-54.34 18.038c-32.025 0-54.34-9.507-54.34-18.038c0-8.535 22.315-18.042 54.34-18.042M188.8 62.823c0-15.863-31.29-24.423-60.733-24.423s-60.733 8.56-60.733 24.423c0 .025.007.054.007.08H67.2v130.61c0 15.645 31.325 24.087 60.8 24.087s60.8-8.442 60.8-24.088V62.902h-.006c0-.025.006-.054.006-.08'
        />
      </svg>
    }
    size='big'
  />
);

export const getConsumedPermissionsOfFineGrainPolicy = async (policy, activeWorkspace, dataset) => {
  let consumedPermissions = [];
  let nextToken = null;
  do {
    let listConsumerDataPermissionsForFgaPolicyResult = await listDataPermissions({
      ownerId: activeWorkspace?.workspaceId,
      region: dataset?.IdInfo?.Region,
      resource: {
        dataCellsFilter: {
          tableCatalogId: policy.tableData.tableCatalogId,
          clusterId: policy.tableData.clusterId,
          databaseName: policy.tableData.databaseName,
          schemaName: policy.tableData.schemaName,
          tableName: policy.tableData.tableName,
          name: policy.tableData.physicalName,
        },
      },
      option: DATA_PERMISSION_CONSUMER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type:
        policy.type === LAKEFORMATION_FGA_POLICY_TYPE
          ? DATA_PERMISSION_LAKE_FORMATION_TYPE
          : DATA_PERMISSION_REDSHIFT_TYPE,
      nextToken: nextToken,
    });
    consumedPermissions.push(...listConsumerDataPermissionsForFgaPolicyResult.dataPermissionList);
    nextToken = listConsumerDataPermissionsForFgaPolicyResult.nextToken;
  } while (nextToken != null);

  return consumedPermissions.filter((permission) => {
    return policy.id === permission.fgapId;
  });
};

export const getAllTableAccessItem = async (
  dataset,
  singleDatasetConsumed,
  iamPrincipals,
  catalogName,
  activeWorkspace,
  cartItems,
) => {
  let allTablesResourceId = `All Tables.${dataset?.IdInfo?.DatabaseName}.${dataset?.IdInfo?.CatalogId}`;
  let singleTablesResourceId = `${dataset?.IdInfo?.TableName}.${dataset?.IdInfo?.DatabaseName}.${dataset?.IdInfo?.CatalogId}`;
  let permissionData = await verifyAllTableAccess(dataset, allTablesResourceId, activeWorkspace, cartItems);
  if (permissionData == undefined) {
    return undefined;
  }
  let databasePermissionId = permissionData.permissionId;
  let allTablesConsumed = permissionData.accountConsumed;
  let publisherRole = permissionData.publisherRole;
  let itemsToReturn = [];

  if (databasePermissionId !== undefined) {
    //block mp data table access
    if (isMPData(dataset?.IdInfo?.CatalogId, dataset?.IdInfo?.DatabaseName, dataset?.IdInfo?.TableName)) {
      console.log('MP data table access is blocked');
      return [];
    }

    let allTablesPermission = {
      label: `Request "All Tables" access from ` + `"${dataset?.IdInfo?.DatabaseName}"`,
      id: allTablesResourceId,
      value: 'workspaceAccess' + allTablesResourceId,
      type: RESOURCE_ITEM,
      datasetName: ALL_TABLES_RES_NAME,
      databaseName: dataset?.IdInfo?.DatabaseName,
      owner: dataset?.PrimaryOwner,
      catalogId: dataset?.IdInfo?.CatalogId,
      catalogName: catalogName,
      permissionId: databasePermissionId,
      workspaceAccess: allTablesConsumed,
      publisherRole: publisherRole,
      principals: permissionData.iamPrincipals,
    };
    let singleTablePermission = {
      label: `Request access to ` + `"${dataset?.IdInfo?.TableName}"`,
      id: singleTablesResourceId,
      value: singleTablesResourceId,
      type: RESOURCE_ITEM,
      datasetName: dataset?.IdInfo?.TableName,
      databaseName: dataset?.IdInfo?.DatabaseName,
      owner: dataset?.PrimaryOwner,
      catalogId: dataset?.IdInfo?.CatalogId,
      catalogName: catalogName,
      permissionId: databasePermissionId,
      workspaceAccess: singleDatasetConsumed,
      publisherRole: publisherRole,
      principals: iamPrincipals,
    };
    itemsToReturn.push(singleTablePermission);
    itemsToReturn.push(allTablesPermission);
    return itemsToReturn;
  }
  return undefined;
};

const verifyAllTableAccess = async (dataset, id, activeWorkspace, cartItems) => {
  let databaseName = dataset?.IdInfo?.DatabaseName;
  let catalogId = dataset?.IdInfo?.CatalogId;
  // get all tables publisher
  let listDatasetPermissionRequestAllTablesPublisher = {
    region: dataset?.IdInfo?.Region,
    resource: {
      table: {
        databaseName: databaseName,
        name: null,
        catalogId: catalogId,
        tableWildcard: {},
      },
    },
    option: DATA_PERMISSION_PUBLISHER_OPTION,
    status: DATA_PERMISSION_STATUS_ACTIVE,
    type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
    nextToken: null,
  };
  // check if current workspace is a all tables consumer
  let listDatasetPermissionRequestAllTablesConsumer = {
    ownerId: activeWorkspace?.workspaceId,
    region: dataset?.IdInfo?.Region,
    resource: {
      table: {
        databaseName: databaseName,
        name: null,
        catalogId: catalogId,
        tableWildcard: {},
      },
    },
    option: DATA_PERMISSION_CONSUMER_OPTION,
    status: DATA_PERMISSION_STATUS_ACTIVE,
    type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
    nextToken: null,
  };

  let accountConsumed = undefined;
  let dataLakePrincipals = [];
  let dataLakeIAMPrincipals = [];
  let permissionId = undefined;
  let publisherRole = undefined;
  try {
    let listDatasetPermissionResponsePublisherAllTables = await listDataPermissions(
      listDatasetPermissionRequestAllTablesPublisher,
    );
    let listDatasetPermissionResponseConsumerAllTables = await listDataPermissions(
      listDatasetPermissionRequestAllTablesConsumer,
    );
    if (
      listDatasetPermissionResponsePublisherAllTables?.dataPermissionList &&
      listDatasetPermissionResponsePublisherAllTables.dataPermissionList.length != 0
    ) {
      permissionId = listDatasetPermissionResponsePublisherAllTables.dataPermissionList[0].dataPermissionId;
      publisherRole = listDatasetPermissionResponsePublisherAllTables.dataPermissionList[0].audit.PublisherRole;
    }
    if (
      listDatasetPermissionResponseConsumerAllTables?.dataPermissionList &&
      listDatasetPermissionResponseConsumerAllTables.dataPermissionList.length != 0
    ) {
      //Get list of datalake Principals
      dataLakePrincipals = [
        ...listDatasetPermissionResponseConsumerAllTables.dataPermissionList.map(
          (dataPermission) => dataPermission.dataLakePrincipal,
        ),
        ...cartItems
          .filter((dataPermission) => dataPermission.id === id)
          .map((dataPermission) => dataPermission.dataLakePrincipal),
      ];
      dataLakeIAMPrincipals = dataLakePrincipals.filter(isValidPrincipalNotRoot);
      console.log(
        `All tables consumer datalakePrincipals: ${dataLakePrincipals}, IamPrincipals:${dataLakeIAMPrincipals}`,
      );
      //Current workspace already consuming the all tables in database
      accountConsumed =
        dataLakePrincipals.filter((dataLakePrincipal) => activeWorkspace.workspaceId.includes(dataLakePrincipal))
          .length !== 0;
    }
    console.log(
      `Found all tables publisher dataPermissionId:${permissionId}, wks consumes datasets:${accountConsumed}`,
    );
  } catch (err) {
    console.log('Exception list all table permissions', err);
    return undefined;
  }
  return {
    permissionId: permissionId,
    accountConsumed: accountConsumed,
    publisherRole: publisherRole,
    iamPrincipals: dataLakeIAMPrincipals,
  };
};

export const getSingleTableAccessDataPermission = async (dataset, resourceId, activeWorkspace, cartItems) => {
  let tableName = dataset?.IdInfo?.TableName;
  let databaseName = dataset?.IdInfo?.DatabaseName;
  let datasetOwnerId = dataset?.PrimaryOwner;
  let catalogId = dataset?.IdInfo?.CatalogId;
  // get single table publisher permission if present
  let listDatasetPermissionRequestPublisher = {
    ownerId: datasetOwnerId,
    region: dataset?.IdInfo?.Region,
    resource: {
      table: {
        databaseName: databaseName,
        name: tableName,
        catalogId: catalogId,
        tableWildcard: null,
      },
    },
    option: DATA_PERMISSION_PUBLISHER_OPTION,
    status: DATA_PERMISSION_STATUS_ACTIVE,
    type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
    nextToken: null,
  };
  // get single table consumer permission of current workspace if present
  let listDatasetPermissionRequestConsumer = {
    ownerId: activeWorkspace?.workspaceId,
    region: dataset?.IdInfo?.Region,
    resource: {
      table: {
        databaseName: databaseName,
        name: tableName,
        catalogId: catalogId,
        tableWildcard: null,
      },
    },
    option: DATA_PERMISSION_CONSUMER_OPTION,
    status: DATA_PERMISSION_STATUS_ACTIVE,
    type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
    nextToken: null,
  };

  let accountConsumed = undefined;
  let dataLakePrincipals = [];
  let dataLakeIAMPrincipals = [];
  let permissionId = undefined;
  let publisherRole = undefined;

  try {
    let listDatasetPermissionResponsePublisher = await listDataPermissions(listDatasetPermissionRequestPublisher);
    let listDatasetPermissionResponseConsumer = await listDataPermissions(listDatasetPermissionRequestConsumer);
    let publisherPermissionAsList = listDatasetPermissionResponsePublisher.dataPermissionList;
    let consumerPermissionAsList = listDatasetPermissionResponseConsumer.dataPermissionList;
    //filter out fga permissions and resource links
    consumerPermissionAsList = consumerPermissionAsList.filter((permission) => {
      return permission.fgapId === null && !permission.metadata.includes(RESOURCE_LINK_PREFIX);
    });
    for (let permission of publisherPermissionAsList) {
      if (
        permission.option == DATA_PERMISSION_PUBLISHER_OPTION &&
        (permission.resource?.table !== undefined ||
          (permission.resource?.tableWithColumns !== undefined &&
            Object.keys(permission.resource.tableWithColumns).length))
      ) {
        permissionId = permission.dataPermissionId;
        publisherRole = permission.audit.PublisherRole;
      }
    }

    if (consumerPermissionAsList && consumerPermissionAsList.length !== 0) {
      //Get list of datalake Principals
      dataLakePrincipals = [
        ...consumerPermissionAsList.map((dataPermission) => dataPermission.dataLakePrincipal),
        ...cartItems
          .filter((dataPermission) => dataPermission.id === resourceId)
          .map((dataPermission) => dataPermission.dataLakePrincipal),
      ];
      dataLakeIAMPrincipals = dataLakePrincipals.filter(isValidPrincipalNotRoot);
      console.log(
        `All single table consumer datalakePrincipals: ${dataLakePrincipals}, IamPrincipals:${dataLakeIAMPrincipals}`,
      );
      //Current workspace already consuming the all tables in database
      accountConsumed =
        dataLakePrincipals.filter((dataLakePrincipal) => activeWorkspace.workspaceId.includes(dataLakePrincipal))
          .length !== 0;
    }
    console.log(
      `Found single table publisher dataPermissionId:${permissionId}, wks consumes datasets:${accountConsumed}`,
    );
  } catch (err) {
    console.log('Exception list tag permission', err);
    return permissionId;
  }
  return {
    permissionId: permissionId,
    accountConsumed: accountConsumed,
    publisherRole: publisherRole,
    iamPrincipals: dataLakeIAMPrincipals,
  };
};

export const checkIfTagIsAlreadyConsumedAndGetPublisherTag = async (
  tag,
  tagWorkspaceId,
  dataset,
  activeWorkspace,
  cartItems,
) => {
  let tagKey = tag.tagKey;
  let tagValue = tag.tagValue;
  let tagAccountId = tag.catalogId;
  let tagId = `${tag.tagKey}.${tag.tagValue}.${tag.catalogId}`;

  let listTagPermissionPublisherRequest;
  let listTagPermissionConsumerRequest;

  if (dataset?.ClusterIdentifier != null) {
    listTagPermissionPublisherRequest = {
      ownerId: tagWorkspaceId,
      region: dataset?.IdInfo?.Region,
      resource: {
        redshiftTagPolicy: {
          tagKey: tagKey,
          tagValue: tagValue,
          catalogId: tagAccountId,
        },
      },
      option: DATA_PERMISSION_PUBLISHER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: REDSHIFT_TAG_TYPE,
      nextToken: null,
    };
    listTagPermissionConsumerRequest = {
      ownerId: activeWorkspace?.workspaceId,
      region: dataset?.IdInfo?.Region,
      resource: {
        redshiftTagPolicy: {
          tagKey: tagKey,
          tagValue: tagValue,
          catalogId: tagAccountId,
        },
      },
      option: DATA_PERMISSION_CONSUMER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: REDSHIFT_TAG_TYPE,
      nextToken: null,
    };
  } else {
    listTagPermissionPublisherRequest = {
      ownerId: tagWorkspaceId,
      region: dataset?.IdInfo?.Region,
      resource: {
        lFTagPolicy: {
          resourceType: TABLE_RESOURCE_TYPE,
          expression: [
            {
              tagKey: tagKey,
              tagValues: [tagValue],
            },
          ],
          catalogId: tagAccountId,
        },
      },
      option: DATA_PERMISSION_PUBLISHER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: LAKEFORMATION_TAG_TYPE,
      nextToken: null,
    };
    listTagPermissionConsumerRequest = {
      ownerId: activeWorkspace.workspaceId,
      region: dataset?.IdInfo?.Region,
      resource: {
        lFTagPolicy: {
          resourceType: TABLE_RESOURCE_TYPE,
          expression: [
            {
              tagKey: tagKey,
              tagValues: [tagValue],
            },
          ],
          catalogId: tagAccountId,
        },
      },
      option: DATA_PERMISSION_CONSUMER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: LAKEFORMATION_TAG_TYPE,
      nextToken: null,
    };
  }

  let accountConsumed = undefined;
  let permissionId = undefined;
  let dataLakePrincipals = [];
  let dataLakeIAMPrincipals = [];
  try {
    let listTagPermissionPublisherResponse = await listDataPermissions(listTagPermissionPublisherRequest);
    let listTagPermissionConsumersResponse = await listDataPermissions(listTagPermissionConsumerRequest);
    // get publisher tag data permission id
    if (
      listTagPermissionPublisherResponse?.dataPermissionList !== undefined &&
      listTagPermissionPublisherResponse?.dataPermissionList.length != 0
    ) {
      permissionId = listTagPermissionPublisherResponse.dataPermissionList[0].dataPermissionId;
    }
    // check if the tag is already consumed
    dataLakePrincipals = [
      ...listTagPermissionConsumersResponse.dataPermissionList.map(
        (dataPermission) => dataPermission.dataLakePrincipal,
      ),
      ...cartItems
        .filter((dataPermission) => dataPermission.id === tagId)
        .map((dataPermission) => dataPermission.dataLakePrincipal),
    ];
    dataLakeIAMPrincipals = dataLakePrincipals.filter(isValidPrincipalNotRoot);
    //Current workspace already consuming the all tables in database
    accountConsumed =
      dataLakePrincipals.filter((dataLakePrincipal) => activeWorkspace.workspaceId.includes(dataLakePrincipal))
        .length !== 0;
  } catch (err) {
    console.log('Exception list tag permission', err);
    return permissionId;
  }
  return {
    permissionId: permissionId,
    consumed: accountConsumed,
    iamPrincipals: dataLakeIAMPrincipals,
  };
};

export const ownerLinkItem = (ownerId, workspaceNameMap) => {
  let groupOrWorkspaceName = getGroupOrWorkspaceName(ownerId, workspaceNameMap);
  return isValidGalaxiGroup(ownerId) ? (
    <Link href={getGalaxiGroupURL(groupOrWorkspaceName)} external={true}>
      {groupOrWorkspaceName}
    </Link>
  ) : (
    <Link href={isValidWorkspace(ownerId) ? createWorkspaceDetailLink(ownerId) : createGroupDetailLink(ownerId)}>
      {groupOrWorkspaceName}
    </Link>
  );
};
